/**
 * NIA Gateway permissions mapping.
 * Maps permission strings to menu visibility.
 * Based on system-dashboard's PermissionsConstant (main branch).
 */

export const NIA_PERMISSIONS = {
  USE_CASES: {
    TEMPLATES: 'View UsecaseTemplateEndpoint',
    INSTANCES: 'View UsecaseInstanceEndpoint',
    SCHEDULES: 'View UsecaseScheduleEndpoint',
    SUBSCRIPTIONS: 'View UsecaseSubscriptionEndpoint',
    EXECUTIONS: 'View UsecaseExecutionEndpoint',
    EXCLUSIONS: 'View ExclusionEndpoit',
  },
  RECOVERY_ACTIONS: {
    TEMPLATES: 'View ActionTemplateEndpoint',
    INSTANCES: 'View ActionInstanceEndpoint',
    EXECUTIONS: 'View ActionExecutionEndpoint',
    RESULTS: 'View ActionResultEndpoint',
  },
  REPORTERS: {
    TEMPLATES: 'View ReporterTemplateEndpoint',
    INSTANCES: 'View ReporterInstanceEndpoint',
    EXECUTIONS: 'View ReporterExecutionEndpoint',
  },
  RESULTS: 'View UsecaseResultEndpoint',
  KPI_DEFINITIONS: 'View KpiEndpoint',
  DASHBOARDS: 'View DashboardEndpoint',
  BI_DASHBOARDS: 'View BIDashboards',
  GLOBAL_STORE: 'View StoreEndpoint',
  VALUE: 'View ValueEndpoit',
  CLUSTER: 'View ClusterEndpoint',
  EPC: {
    NODES: 'View NodeEndpoint',
    CM: 'View EpcCmEndpoint',
  },
  FEEDBACK: 'View FeedbackEndpoint',
  ADMINISTRATION: {
    USERS: 'View UserEndpoint',
    ROLES: 'View RoleEndpoint',
    PERMISSIONS: 'View PermissionEndpoint',
    FEATURES: 'View FeaturesEndpoint',
  },
} as const;

/**
 * Permissions granted per NIA role.
 * Based on actual role-permission assignments in NIA Gateway DB.
 */
const ROLE_PERMISSIONS: Record<string, string[]> = {
  Administrator: [
    NIA_PERMISSIONS.USE_CASES.TEMPLATES,
    NIA_PERMISSIONS.USE_CASES.INSTANCES,
    NIA_PERMISSIONS.USE_CASES.SCHEDULES,
    NIA_PERMISSIONS.USE_CASES.SUBSCRIPTIONS,
    NIA_PERMISSIONS.USE_CASES.EXECUTIONS,
    NIA_PERMISSIONS.USE_CASES.EXCLUSIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.TEMPLATES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.INSTANCES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.EXECUTIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.RESULTS,
    NIA_PERMISSIONS.REPORTERS.TEMPLATES,
    NIA_PERMISSIONS.REPORTERS.INSTANCES,
    NIA_PERMISSIONS.REPORTERS.EXECUTIONS,
    NIA_PERMISSIONS.RESULTS,
    NIA_PERMISSIONS.KPI_DEFINITIONS,
    NIA_PERMISSIONS.DASHBOARDS,
    NIA_PERMISSIONS.BI_DASHBOARDS,
    NIA_PERMISSIONS.GLOBAL_STORE,
    NIA_PERMISSIONS.VALUE,
    NIA_PERMISSIONS.CLUSTER,
    NIA_PERMISSIONS.EPC.NODES,
    NIA_PERMISSIONS.EPC.CM,
    NIA_PERMISSIONS.FEEDBACK,
    NIA_PERMISSIONS.ADMINISTRATION.USERS,
    NIA_PERMISSIONS.ADMINISTRATION.ROLES,
    NIA_PERMISSIONS.ADMINISTRATION.PERMISSIONS,
    NIA_PERMISSIONS.ADMINISTRATION.FEATURES,
  ],
  Designer: [
    NIA_PERMISSIONS.USE_CASES.TEMPLATES,
    NIA_PERMISSIONS.USE_CASES.INSTANCES,
    NIA_PERMISSIONS.USE_CASES.SCHEDULES,
    NIA_PERMISSIONS.USE_CASES.SUBSCRIPTIONS,
    NIA_PERMISSIONS.USE_CASES.EXECUTIONS,
    NIA_PERMISSIONS.USE_CASES.EXCLUSIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.TEMPLATES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.INSTANCES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.EXECUTIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.RESULTS,
    NIA_PERMISSIONS.REPORTERS.TEMPLATES,
    NIA_PERMISSIONS.REPORTERS.INSTANCES,
    NIA_PERMISSIONS.REPORTERS.EXECUTIONS,
    NIA_PERMISSIONS.RESULTS,
    NIA_PERMISSIONS.KPI_DEFINITIONS,
    NIA_PERMISSIONS.DASHBOARDS,
    NIA_PERMISSIONS.BI_DASHBOARDS,
    NIA_PERMISSIONS.GLOBAL_STORE,
    NIA_PERMISSIONS.VALUE,
    NIA_PERMISSIONS.CLUSTER,
    NIA_PERMISSIONS.EPC.NODES,
    NIA_PERMISSIONS.EPC.CM,
    NIA_PERMISSIONS.FEEDBACK,
  ],
  Operator: [
    NIA_PERMISSIONS.USE_CASES.TEMPLATES,
    NIA_PERMISSIONS.USE_CASES.INSTANCES,
    NIA_PERMISSIONS.USE_CASES.SCHEDULES,
    NIA_PERMISSIONS.USE_CASES.SUBSCRIPTIONS,
    NIA_PERMISSIONS.USE_CASES.EXECUTIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.TEMPLATES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.INSTANCES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.EXECUTIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.RESULTS,
    NIA_PERMISSIONS.REPORTERS.TEMPLATES,
    NIA_PERMISSIONS.REPORTERS.INSTANCES,
    NIA_PERMISSIONS.REPORTERS.EXECUTIONS,
    NIA_PERMISSIONS.RESULTS,
    NIA_PERMISSIONS.KPI_DEFINITIONS,
    NIA_PERMISSIONS.DASHBOARDS,
    NIA_PERMISSIONS.GLOBAL_STORE,
    NIA_PERMISSIONS.FEEDBACK,
  ],
  Viewer: [
    NIA_PERMISSIONS.USE_CASES.TEMPLATES,
    NIA_PERMISSIONS.USE_CASES.INSTANCES,
    NIA_PERMISSIONS.USE_CASES.SCHEDULES,
    NIA_PERMISSIONS.USE_CASES.SUBSCRIPTIONS,
    NIA_PERMISSIONS.USE_CASES.EXECUTIONS,
    NIA_PERMISSIONS.USE_CASES.EXCLUSIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.TEMPLATES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.INSTANCES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.EXECUTIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.RESULTS,
    NIA_PERMISSIONS.REPORTERS.TEMPLATES,
    NIA_PERMISSIONS.REPORTERS.INSTANCES,
    NIA_PERMISSIONS.REPORTERS.EXECUTIONS,
    NIA_PERMISSIONS.RESULTS,
    NIA_PERMISSIONS.KPI_DEFINITIONS,
    NIA_PERMISSIONS.DASHBOARDS,
    NIA_PERMISSIONS.BI_DASHBOARDS,
    NIA_PERMISSIONS.GLOBAL_STORE,
    NIA_PERMISSIONS.VALUE,
    NIA_PERMISSIONS.CLUSTER,
    NIA_PERMISSIONS.EPC.NODES,
    NIA_PERMISSIONS.EPC.CM,
    NIA_PERMISSIONS.FEEDBACK,
  ],
  gwAdmin: [
    NIA_PERMISSIONS.USE_CASES.TEMPLATES,
    NIA_PERMISSIONS.USE_CASES.INSTANCES,
    NIA_PERMISSIONS.USE_CASES.SCHEDULES,
    NIA_PERMISSIONS.USE_CASES.SUBSCRIPTIONS,
    NIA_PERMISSIONS.USE_CASES.EXECUTIONS,
    NIA_PERMISSIONS.USE_CASES.EXCLUSIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.TEMPLATES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.INSTANCES,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.EXECUTIONS,
    NIA_PERMISSIONS.RECOVERY_ACTIONS.RESULTS,
    NIA_PERMISSIONS.REPORTERS.TEMPLATES,
    NIA_PERMISSIONS.REPORTERS.INSTANCES,
    NIA_PERMISSIONS.REPORTERS.EXECUTIONS,
    NIA_PERMISSIONS.RESULTS,
    NIA_PERMISSIONS.KPI_DEFINITIONS,
    NIA_PERMISSIONS.DASHBOARDS,
    NIA_PERMISSIONS.GLOBAL_STORE,
    NIA_PERMISSIONS.FEEDBACK,
    NIA_PERMISSIONS.ADMINISTRATION.USERS,
    NIA_PERMISSIONS.ADMINISTRATION.ROLES,
    NIA_PERMISSIONS.ADMINISTRATION.PERMISSIONS,
  ],
  'Customer-Viewer': [NIA_PERMISSIONS.BI_DASHBOARDS],
  'Customer-Designer': [NIA_PERMISSIONS.BI_DASHBOARDS],
};

const NIA_PERMISSIONS_KEY = 'nia-permissions';

/**
 * Resolve permissions from NIA token scope (list of role names).
 */
export function resolveNiaPermissions(scope: string): string[] {
  const roles = scope.split(' ');
  const permissions = new Set<string>();
  for (const role of roles) {
    const rolePerms = ROLE_PERMISSIONS[role];
    if (rolePerms) {
      rolePerms.forEach((p) => permissions.add(p));
    }
  }
  return Array.from(permissions);
}

/**
 * Store NIA permissions in localStorage.
 */
export function storeNiaPermissions(permissions: string[]): void {
  localStorage.setItem(NIA_PERMISSIONS_KEY, JSON.stringify(permissions));
}

/**
 * Get stored NIA permissions.
 */
export function getNiaPermissions(): string[] {
  try {
    const stored = localStorage.getItem(NIA_PERMISSIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Check if user has a specific NIA permission.
 */
export function hasNiaPermission(permission: string): boolean {
  return getNiaPermissions().includes(permission);
}
